/**
 * Simplified employee salary: annual basic + bonus only.
 * Legacy HRA/PF/etc. amounts are still read for payroll on existing records.
 */

export const roundTo2Decimals = (num) => Math.round(num * 100) / 100;

export const getAnnualBasic = (emp) =>
  Number(emp?.basicSalaryAmount ?? emp?.basicSalary ?? emp?.salary_info?.basic ?? 0) || 0;

export const getAnnualBonus = (emp) =>
  Number(emp?.bonusAmount ?? emp?.bonus ?? emp?.salary_info?.bonus ?? 0) || 0;

/** Annual CTC: basic + bonus, or stored ctc when present */
export const getAnnualCTC = (emp) => {
  const stored = Number(emp?.ctc);
  if (Number.isFinite(stored) && stored > 0) return stored;
  return getAnnualBasic(emp) + getAnnualBonus(emp);
};

export const calculateAnnualCTC = (basicSalary, bonus) =>
  (Number(basicSalary) || 0) + (Number(bonus) || 0);

/** Legacy component annual amounts (for employees created before simplification) */
const LEGACY_COMPONENTS = [
  { key: 'hra', label: 'HRA', fields: ['hraAmount', 'hra'] },
  { key: 'conveyance', label: 'Conveyance', fields: ['conveyanceAmount', 'conveyanceAllowance', 'conveyance'] },
  { key: 'pf', label: 'Employer PF', fields: ['employerPFAmount', 'employerPF', 'pf'] },
  { key: 'gratuity', label: 'Gratuity', fields: ['gratuityAmount', 'gratuity'] },
  { key: 'special', label: 'Special Allowance', fields: ['specialAllowanceAmount', 'specialAllowance', 'special'] },
];

const SALARY_INFO_KEYS = {
  hraAmount: 'hra',
  hra: 'hra',
  conveyanceAmount: 'conveyance',
  conveyanceAllowance: 'conveyance',
  conveyance: 'conveyance',
  employerPFAmount: 'pf',
  employerPF: 'pf',
  pf: 'pf',
  gratuityAmount: 'gratuity',
  gratuity: 'gratuity',
  specialAllowanceAmount: 'special',
  specialAllowance: 'special',
  special: 'special',
};

const getLegacyAnnualAmount = (emp, fields) => {
  for (const f of fields) {
    const v = Number(emp?.[f]);
    if (Number.isFinite(v) && v > 0) return v;
  }
  const info = emp?.salary_info;
  if (info) {
    for (const f of fields) {
      const infoKey = SALARY_INFO_KEYS[f] || f;
      const v = Number(info[infoKey]);
      if (Number.isFinite(v) && v > 0) return v;
    }
  }
  return 0;
};

/** Sum of legacy HRA/PF/etc. annual amounts still on employee record */
export const getLegacyAnnualTotal = (emp) => {
  if (!emp) return 0;
  return LEGACY_COMPONENTS.reduce((sum, { fields }) => sum + getLegacyAnnualAmount(emp, fields), 0);
};

/** Whether employee still has old split salary fields with values */
export const employeeNeedsSalaryMigration = (emp) => {
  if (!emp) return false;
  const legacy = getLegacyAnnualTotal(emp);
  if (legacy <= 0) return false;
  const hasBonus = getAnnualBonus(emp) > 0 || emp.bonusAmount != null || emp.salary_info?.bonus != null;
  return !hasBonus;
};

/**
 * Fold legacy components into annual basic; keep explicit bonus.
 * Returns payload fields to merge on employee update.
 */
export const normalizeEmployeeSalaryForMigration = (emp) => {
  const currentBasic = getAnnualBasic(emp);
  const currentBonus = getAnnualBonus(emp);
  const legacyTotal = getLegacyAnnualTotal(emp);

  if (!employeeNeedsSalaryMigration(emp)) {
    return { migrated: false, ...buildSalaryPayload(currentBasic, currentBonus) };
  }

  const storedCtc = Number(emp?.ctc);
  let newBasic =
    legacyTotal > 0
      ? (currentBasic > 0 ? currentBasic + legacyTotal : legacyTotal)
      : currentBasic;

  if (Number.isFinite(storedCtc) && storedCtc > 0 && newBasic < storedCtc && currentBonus === 0) {
    newBasic = storedCtc;
  }

  return { migrated: true, ...buildSalaryPayload(newBasic, currentBonus) };
};

export const getLegacyMonthlyRows = (emp) => {
  if (!emp) return [];
  return LEGACY_COMPONENTS.map(({ label, fields }) => {
    const annual = getLegacyAnnualAmount(emp, fields);
    if (annual <= 0) return null;
    return {
      type: label,
      amount: roundTo2Decimals(annual / 12).toFixed(2),
      remark: 'Legacy component',
      fixed: true,
      removable: false,
    };
  }).filter(Boolean);
};

/**
 * Monthly salary rows for payroll — Basic + Bonus only (annual amounts from employee profile).
 */
export const getMonthlySalaryRows = (emp) => {
  if (!emp) return [];

  const basic = getAnnualBasic(emp);
  const bonus = getAnnualBonus(emp);

  return [
    {
      type: 'Basic Salary',
      amount: roundTo2Decimals(basic / 12).toFixed(2),
      remark: 'From employee profile',
      fixed: true,
      removable: false,
    },
    {
      type: 'Bonus',
      amount: roundTo2Decimals(bonus / 12).toFixed(2),
      remark: 'From employee profile',
      fixed: true,
      removable: false,
    },
  ].filter((r) => parseFloat(r.amount) > 0 || r.type === 'Basic Salary');
};

/** Monthly breakdown for employee-facing dashboard */
export const getMonthlySalaryBreakdown = (emp) => {
  if (!emp) return [];

  const basic = getAnnualBasic(emp);
  const bonus = getAnnualBonus(emp);
  const items = [
    { component: 'Basic Salary', amount: roundTo2Decimals(basic / 12).toFixed(0) },
    { component: 'Bonus', amount: roundTo2Decimals(bonus / 12).toFixed(0) },
  ];

  const legacy = getLegacyMonthlyRows(emp);
  const hasExplicitBonus = bonus > 0 || emp.bonusAmount != null || emp.salary_info?.bonus != null;

  if (legacy.length > 0 && !hasExplicitBonus) {
    legacy.forEach((row) => {
      items.push({ component: row.type, amount: parseFloat(row.amount).toFixed(0) });
    });
  }

  return items.filter((item) => parseFloat(item.amount) > 0 || item.component === 'Basic Salary');
};

/** Payload fields when saving employee salary */
export const buildSalaryPayload = (basicSalary, bonus) => {
  const basicAmt = parseFloat(basicSalary) || 0;
  const bonusAmt = parseFloat(bonus) || 0;
  return {
    ctc: basicAmt + bonusAmt,
    basicSalaryAmount: basicAmt,
    bonusAmount: bonusAmt,
    hraAmount: 0,
    conveyanceAmount: 0,
    employerPFAmount: 0,
    gratuityAmount: 0,
    specialAllowanceAmount: 0,
    salary_info: {
      basic: basicAmt,
      bonus: bonusAmt,
      hra: 0,
      conveyance: 0,
      pf: 0,
      gratuity: 0,
      special: 0,
    },
  };
};
